
import { TikTokVideo } from "@/types/api";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { User } from "lucide-react";
import { getAuthorDisplayName, formatDuration, getAuthorProfileUrl } from "@/utils/videoHelpers";

interface VideoCardProps {
  video: TikTokVideo;
  isChecked: boolean;
  onToggleSelect: (id: string) => void;
  onDeleteVideo?: (id: string) => void;
}

export function VideoCard({
  video,
  isChecked,
  onToggleSelect,
  onDeleteVideo,
}: VideoCardProps) {
  const authorName = getAuthorDisplayName(video.author);
  const avatarUrl = typeof video.author === 'object' ? video.author.avatarThumb : undefined;
  const truncatedDescription = video.description.length > 100 
    ? `${video.description.substring(0, 97)}...` 
    : video.description;
  const videoUrl = `https://www.tiktok.com/@${authorName}/video/${video.id}`;

  return (
    <Card className="bg-[#111827] border-gray-800 overflow-hidden">
      <div className="relative">
        <a
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block relative cursor-pointer group"
        >
          <img
            src={video.thumbnail}
            alt="Video thumbnail"
            className="w-full aspect-[9/16] object-cover transition-opacity group-hover:opacity-90"
            onError={(e) => ((e.target as HTMLImageElement).src = "/placeholder.svg")}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <span className="text-white/0 group-hover:text-white/90 text-sm font-medium transition-all">
              Watch on TikTok
            </span>
          </div>
        </a>
        <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded text-xs text-white">
          {formatDuration(video.duration)}
        </div>
        <div className="absolute top-2 left-2">
          <Checkbox
            checked={isChecked}
            onCheckedChange={() => onToggleSelect(video.id)}
            className="bg-black/60 border-gray-400"
          />
        </div>
      </div>
      
      <CardContent className="p-4">
        <div className="flex items-start gap-3 mb-2">
          <a 
            href={`https://www.tiktok.com/@${authorName}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group"
          >
            <Avatar className="h-8 w-8 transition-opacity group-hover:opacity-80">
              <AvatarImage src={avatarUrl} alt={authorName} />
              <AvatarFallback>
                <User className="h-4 w-4 text-gray-400" />
              </AvatarFallback>
            </Avatar>
          </a>
          <div className="flex-1">
            <h3 className="font-medium text-sm text-white leading-tight">
              <a 
                href={`https://www.tiktok.com/@${authorName}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#00E5FF] transition-colors"
              >
                {authorName}
              </a>
            </h3>
            <p className="text-xs text-gray-400 mt-1 line-clamp-2">
              {truncatedDescription}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
